// src/hooks/useMicrophone.ts
import { useState, useRef, useCallback, useEffect } from 'react';
import { transcribeSpeech } from '../api/api';
import { STT_RECORDING_STARTED_EVENT, stopAllAudioPlayback } from '../services/audioService';
import type { VoiceInputMessages } from './voiceInputMessages';

export type MicState = 'idle' | 'connecting' | 'recording' | 'processing';

interface UseMicrophoneOptions {
  onTranscription: (text: string) => void;
  onError?: (error: string) => void;
  onAnnounce?: (message: string) => void; // Screen reader announcements (WCAG SC 4.1.3)
  maxRecordingMs?: number; // Max recording time in ms (default: 30000)
  silenceTimeoutMs?: number; // Silence auto-stop timeout in ms (default: 3000)
  messages: VoiceInputMessages;
}

// Minimum recording duration to ensure Azure OpenAI receives enough audio frames.
// Short recordings (< 600ms) produce nearly-empty WebM containers that the API rejects.
const MIN_RECORDING_MS = 700;
const SILENCE_RMS_THRESHOLD = 0.01;

export function useMicrophone({
  onTranscription,
  onError,
  onAnnounce,
  maxRecordingMs = 30000,
  silenceTimeoutMs = 3000,
  messages,
}: UseMicrophoneOptions) {
  const [micState, setMicState] = useState<MicState>('idle');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const minDurationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recordingStartTimeRef = useRef<number>(0);
  const mimeTypeRef = useRef<string>('audio/webm');
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const mediaSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const silenceIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastNonSilentAtRef = useRef<number>(0);
  const signalAnalysisAvailableRef = useRef(false);
  const speechSignalDetectedRef = useRef(false);
  const recordingAttemptRef = useRef(0);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      recordingAttemptRef.current += 1;
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.onstop = null;
        mediaRecorderRef.current.onerror = null;
        mediaRecorderRef.current.ondataavailable = null;
        if (mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop();
      }
      streamRef.current?.getTracks().forEach(track => track.stop());
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (minDurationTimeoutRef.current) clearTimeout(minDurationTimeoutRef.current);
      if (silenceIntervalRef.current) clearInterval(silenceIntervalRef.current);
      if (mediaSourceRef.current) mediaSourceRef.current.disconnect();
      if (audioContextRef.current) audioContextRef.current.close();
    };
  }, []);

  const stopSilenceDetection = useCallback(() => {
    if (silenceIntervalRef.current) {
      clearInterval(silenceIntervalRef.current);
      silenceIntervalRef.current = null;
    }
    if (mediaSourceRef.current) {
      mediaSourceRef.current.disconnect();
      mediaSourceRef.current = null;
    }
    analyserRef.current = null;
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  }, []);

  const stopRecording = useCallback(() => {
    console.log('[useMicrophone] stopRecording called');
    if (minDurationTimeoutRef.current) {
      clearTimeout(minDurationTimeoutRef.current);
      minDurationTimeoutRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    stopSilenceDetection();

    // Call requestData() to force Chrome to flush its internal encoder buffer,
    // then stop(). Without requestData(), Chrome may produce an empty WebM
    // (just EBML headers, no audio clusters). The concatenated output from
    // requestData()+stop() is a valid continuous byte stream per the W3C spec.
    // The earlier "corruption" issue was actually a MIME-type bug (now fixed).
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      try {
        mediaRecorderRef.current.requestData();
      } catch (e) {
        console.warn('[useMicrophone] requestData failed:', e);
      }
      mediaRecorderRef.current.stop();
    } else if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.stop();
    }
  }, [stopSilenceDetection]);

  /**
   * Attempt to stop recording, but ensure at least MIN_RECORDING_MS has elapsed.
   * If called too early, schedules a deferred stop.
   */
  const stopRecordingWithMinDuration = useCallback(() => {
    const elapsed = Date.now() - recordingStartTimeRef.current;
    if (elapsed >= MIN_RECORDING_MS) {
      stopRecording();
    } else {
      const remaining = MIN_RECORDING_MS - elapsed;
      console.log(`[useMicrophone] Deferring stop by ${remaining}ms to meet minimum duration`);
      minDurationTimeoutRef.current = setTimeout(() => {
        minDurationTimeoutRef.current = null;
        stopRecording();
      }, remaining);
    }
  }, [stopRecording]);

  const startSilenceDetection = useCallback((stream: MediaStream) => {
    try {
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.8;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      mediaSourceRef.current = source;
      lastNonSilentAtRef.current = Date.now();
      signalAnalysisAvailableRef.current = true;

      const buffer = new Uint8Array(analyser.fftSize);
      const rmsThreshold = SILENCE_RMS_THRESHOLD;

      silenceIntervalRef.current = setInterval(() => {
        if (!mediaRecorderRef.current || mediaRecorderRef.current.state !== 'recording') {
          return;
        }

        analyser.getByteTimeDomainData(buffer);
        let sumSquares = 0;
        for (let i = 0; i < buffer.length; i += 1) {
          const normalized = (buffer[i] - 128) / 128;
          sumSquares += normalized * normalized;
        }
        const rms = Math.sqrt(sumSquares / buffer.length);

        if (rms >= rmsThreshold) {
          speechSignalDetectedRef.current = true;
          lastNonSilentAtRef.current = Date.now();
          return;
        }

        if (!speechSignalDetectedRef.current) {
          return;
        }

        const silenceDuration = Date.now() - lastNonSilentAtRef.current;
        if (silenceDuration >= silenceTimeoutMs) {
          stopRecordingWithMinDuration();
        }
      }, 200);
    } catch (error) {
      console.warn('[useMicrophone] Silence detection unavailable:', error);
    }
  }, [silenceTimeoutMs, stopRecordingWithMinDuration]);

  const startRecording = useCallback(async () => {
    const recordingAttempt = ++recordingAttemptRef.current;
    console.log('[useMicrophone] startRecording called');
    // Guardrail: always stop active TTS/audio at the point microphone capture starts.
    stopAllAudioPlayback();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event(STT_RECORDING_STARTED_EVENT));
    }
    setMicState('connecting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        }
      });
      if (recordingAttempt !== recordingAttemptRef.current) {
        stream.getTracks().forEach(track => track.stop());
        return;
      }
      streamRef.current = stream;

      // Reset chunks and timer
      chunksRef.current = [];
      setElapsedSeconds(0);
      signalAnalysisAvailableRef.current = false;
      speechSignalDetectedRef.current = false;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/mp4';

      mimeTypeRef.current = mimeType;
      console.log('[useMicrophone] Using mimeType:', mimeType);

      const mediaRecorder = new MediaRecorder(stream, { mimeType });

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
          console.log('[useMicrophone] Chunk added, size:', e.data.size);
        }
      };

      mediaRecorder.onstop = async () => {
        console.log('[useMicrophone] mediaRecorder.onstop triggered');
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
        stopSilenceDetection();

        setMicState('processing');

        const durationMs = Date.now() - recordingStartTimeRef.current;
        console.log(`[useMicrophone] Recording stopped after ${durationMs}ms`);

        // After requestData()+stop(), Chrome produces a trailing micro-chunk
        // (typically <100 bytes) from stop() that corrupts the WebM container.
        // Remove it — the real audio data comes from the requestData() flush.
        const chunks = chunksRef.current;
        if (chunks.length > 1 && chunks[chunks.length - 1].size < 100) {
          console.log('[useMicrophone] Removing trailing micro-chunk:', chunks[chunks.length - 1].size, 'bytes');
          chunks.pop();
        }

        const audioBlob = new Blob(chunks, { type: mimeType });
        console.log('[useMicrophone] Final audioBlob size:', audioBlob.size);

        if (audioBlob.size < 100 || durationMs < 300) {
          console.warn('[useMicrophone] Recording too short, size:', audioBlob.size, 'duration:', durationMs);
          setMicState('idle');
          onError?.(messages.recordingTooShortMessage);
          return;
        }

        // Do not send an all-silence recording to the transcription model.
        // Audio models can otherwise return plausible text for silence/noise.
        // Preserve the existing upload fallback when browser signal analysis
        // was unavailable, since we cannot safely classify that recording.
        if (signalAnalysisAvailableRef.current && !speechSignalDetectedRef.current) {
          setMicState('idle');
          setElapsedSeconds(0);
          onError?.(messages.noSpeechDetectedMessage);
          return;
        }

        try {
          console.log('[useMicrophone] Calling transcribeSpeech API...');
          const text = await transcribeSpeech(audioBlob);
          if (recordingAttempt !== recordingAttemptRef.current) return;
          if (text && text.trim()) {
            onTranscription(text);
            onAnnounce?.(`${messages.transcriptionResultPrefix} ${text.trim()}`);
          } else {
            onError?.(messages.noSpeechDetectedMessage);
          }
        } catch (err) {
          if (recordingAttempt !== recordingAttemptRef.current) return;
          console.error('[useMicrophone] Transcription API error:', err);
          onError?.(messages.transcriptionFailedMessage);
        } finally {
          if (recordingAttempt === recordingAttemptRef.current) {
            setMicState('idle');
            setElapsedSeconds(0);
          }
        }
      };

      mediaRecorder.onerror = (e) => {
        console.error('[useMicrophone] MediaRecorder error:', e);
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
        stopSilenceDetection();
        setMicState('idle');
        setElapsedSeconds(0);
        onError?.(messages.recordingFailedMessage);
      };

      mediaRecorderRef.current = mediaRecorder;
      recordingStartTimeRef.current = Date.now();

      // Start without timeslice — stop() will fire a single ondataavailable
      // with the complete, valid WebM container. This is more reliable across
      // browsers than timeslice-based recording which can produce empty chunks.
      mediaRecorder.start();

      setMicState('recording');
      onAnnounce?.(messages.recordingAnnouncement);
      startSilenceDetection(stream);

      timerIntervalRef.current = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);

      timeoutRef.current = setTimeout(() => {
        stopRecording();
      }, maxRecordingMs);

    } catch (err) {
      if (recordingAttempt !== recordingAttemptRef.current) return;
      console.error('[useMicrophone] getUserMedia error:', err);
      setMicState('idle');
      let errorMsg = messages.microphoneAccessFailedMessage;
      if (err instanceof Error && err.name === 'NotAllowedError') {
        errorMsg = messages.microphoneAccessDeniedMessage;
      } else if (err instanceof Error && err.name === 'NotFoundError') {
        errorMsg = messages.noMicrophoneDetectedMessage;
      }
      onError?.(errorMsg);
    }
  }, [messages, onTranscription, onError, onAnnounce, maxRecordingMs, stopRecording, startSilenceDetection, stopSilenceDetection]);

  const toggleRecording = useCallback(() => {
    console.log('[useMicrophone] toggleRecording called, micState:', micState);
    if (micState === 'idle' || micState === 'connecting') {
      if (micState === 'connecting') return; // already starting
      startRecording();
    } else if (micState === 'recording') {
      stopRecordingWithMinDuration();
    }
  }, [micState, startRecording, stopRecordingWithMinDuration]);

  const cancelRecording = useCallback(() => {
    recordingAttemptRef.current += 1;
    console.log('[useMicrophone] cancelRecording called');
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
    if (timerIntervalRef.current) { clearInterval(timerIntervalRef.current); timerIntervalRef.current = null; }
    if (minDurationTimeoutRef.current) { clearTimeout(minDurationTimeoutRef.current); minDurationTimeoutRef.current = null; }
    stopSilenceDetection();
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.onstop = null;
      if (mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setMicState('idle');
    setElapsedSeconds(0);
    onAnnounce?.(messages.recordingCancelledAnnouncement);
  }, [messages.recordingCancelledAnnouncement, onAnnounce, stopSilenceDetection]);

  const maxSeconds = Math.floor(maxRecordingMs / 1000);

  return {
    micState,
    startRecording,
    stopRecording,
    toggleRecording,
    cancelRecording,
    isRecording: micState === 'recording',
    isProcessing: micState === 'processing',
    elapsedSeconds,
    maxSeconds,
    timerDisplay: `${elapsedSeconds}s / ${maxSeconds}s`,
  };
}
